// StageChip — Treatment A: outline-pill stage badge for detail headers
// + hero rows.
//
// Looks up label / tone_color / microcopy from getStageLabel(stage_id, entity).
// Renders an outline pill in the stage's tone color, with the resolved label
// inside. When `size === 'lg'`, also renders the resolved microcopy as a
// sub-line underneath via StageMicrocopy.
//
// The outline color is sourced from the stage's tone_color group gradient
// (see specs/_product/dashboard-v2-tokens.md §3.5 for the locked palette).
// Falls back gracefully if the (entity, stage_id) tuple is unknown — see
// stage-labels.ts §"worst case path".
//
// Per specs/_product/dashboard-v2-stage-treatments.md (Treatment A).
// Per feedback_stage_labels_human_readable.md — never renders raw enum.
// Per feedback_field_primitive_components_canonical.md — canonical primitive
// in the composition registry.

import { getStageLabel, type Entity } from "../../lib/stage-labels";
import { StageMicrocopy } from "./StageMicrocopy";

export type StageChipSize = "sm" | "md" | "lg";

export interface StageChipProps {
  stage_id: string;
  entity: Entity;
  /** Optional placeholder values for {key} interpolation in microcopy. */
  placeholders?: Record<string, string | number>;
  /** sm: 22px; md (default): 26px; lg: 32px + microcopy sub-line. */
  size?: StageChipSize;
  /** Optional className — appended to the chip element's class list. */
  className?: string;
}

const HEIGHTS: Record<StageChipSize, number> = { sm: 22, md: 26, lg: 32 };
const FONT_SIZES: Record<StageChipSize, number> = { sm: 11, md: 12, lg: 13 };
const PADDING: Record<StageChipSize, string> = { sm: "0 8px", md: "0 10px", lg: "0 12px" };

export function StageChip({ stage_id, entity, placeholders, size = "md", className }: StageChipProps) {
  const { label, microcopy, tone_color } = getStageLabel(stage_id, entity, placeholders);

  // Tone color → CSS custom property name. e.g. tone_color="g-events" →
  // var(--color-g-events-from). The "-from" end of each gradient pair has
  // sufficient contrast for outline + label text.
  const toneVar = `var(--color-${tone_color}-from)`;

  const chip = (
    <span
      className={className}
      data-stage-id={stage_id}
      data-entity={entity}
      data-tone={tone_color}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: HEIGHTS[size],
        padding: PADDING[size],
        fontSize: FONT_SIZES[size],
        fontWeight: 500,
        letterSpacing: "0.01em",
        lineHeight: 1,
        whiteSpace: "nowrap",
        color: toneVar,
        background: "var(--color-surface)",
        border: `1px solid ${toneVar}`,
        borderRadius: 9999,
      }}
    >
      {label}
    </span>
  );

  if (size !== "lg" || !microcopy) return chip;

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
      {chip}
      <StageMicrocopy text={microcopy} />
    </span>
  );
}
